/**
 * Watch bridge CWD and rebuild the trigram file index after filesystem settles (debounced).
 * Uses Node `fs.watch` with `recursive: true` where the platform supports it (fewer FDs than chokidar).
 */

import path from 'node:path';
import { getBridgeRoot } from './cwd/index.js';
import {
  createFileIndexWatchRebuildState,
  runFileIndexWatchRebuild,
  scheduleFileIndexWatchRebuild,
} from './index/watch-rebuild.js';
import {
  createFileIndexFsWatcher,
  FILE_INDEX_WATCH_DEBOUNCE_MS,
} from './index/file-index-fs-watcher.js';

export { runInitialIndexBuilds, scheduleInitialIndexBuilds } from './index/file-index-initial-builds.js';

/**
 * Start watching `cwd` (default process.cwd). Rebuilds index on changes (debounced).
 * Initial builds are not started here — call {@link scheduleInitialIndexBuilds} after the bridge
 * WebSocket is open so connection setup stays responsive.
 * @returns Stop function (call on bridge shutdown).
 */
export function startFileIndexWatcher(cwd: string = getBridgeRoot()): () => void {
  const resolved = path.resolve(cwd);
  const rebuildState = createFileIndexWatchRebuildState();

  let timer: ReturnType<typeof setTimeout> | null = null;

  const schedule = (relPath: string | null): void => {
    scheduleFileIndexWatchRebuild(resolved, rebuildState, relPath);
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      runFileIndexWatchRebuild(resolved, rebuildState);
    }, FILE_INDEX_WATCH_DEBOUNCE_MS);
  };

  const watcher = createFileIndexFsWatcher(resolved, schedule);

  return () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    watcher.close();
  };
}
